<template>
  <div class="MedicalQuestionnaireForm">
    <h2 class="gl_form-title small">
      {{ t('copy_life_questionnaire-form_title') }}
    </h2>

    <div
      v-for="(q, key) in questionnaire"
      :key="key"
      class="question"
    >
      <h3
        v-if="hasHtmlTags(t(`${i18nRoot}_${camelCase2kebabCase(key)}-question`))"
        class="gl_heading secondary text-black"
        v-html="t(`${i18nRoot}_${camelCase2kebabCase(key)}-question`)"
      />

      <h3
        v-else
        class="gl_heading secondary"
      >
        {{ t(`${i18nRoot}_${camelCase2kebabCase(key)}-question`) }}
      </h3>

      <CardInfoComponent
        v-if="te(`${i18nRoot}_${camelCase2kebabCase(key)}-message`)"
        icon="icon-informacion"
      >
        {{ t(`${i18nRoot}_${camelCase2kebabCase(key)}-message`) }}
      </CardInfoComponent>

      <YesNoComponent
        :id="camelCase2kebabCase(key)"
        :rules="[yesNoRequired]"
        v-model="q.checked"
      />

      <component
        v-if="q.checked"
        :is="COMPONENTS[q.values.componentName as QuestionKeyType]"
        :id="camelCase2kebabCase(key)"
        v-model="q.values"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onBeforeMount } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import YesNoComponent from '@/modules/core/components/formFields/YesNoComponent.vue'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import LifeInsuredMedicalQuestionnaire from '../../models/APP/LifeInsuredMedicalQuestionnaire'
  import QuestionDescription from '@/modules/common/common/components/questions/QuestionDescription.vue'
  import QuestionAdmission from '@/modules/common/common/components/questions/QuestionAdmission.vue'
  import QuestionUsage from '@/modules/common/common/components/questions/QuestionUsage.vue'
  import QuestionPathology from '@/modules/common/common/components/questions/QuestionPathology.vue'
  import { camelCase2kebabCase } from '@/utils/converterUtils'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'

  const { t, te } = useI18n()
  const { yesNoRequired } = useRules()
  const loadingStore = useLoadingStore()

  const i18nRoot = 'copy_questionnaire-form'

  type QuestionKeyType = keyof typeof COMPONENTS

  defineExpose({ triggerSubmit })

  const COMPONENTS = {
    QuestionDescription,
    QuestionAdmission,
    QuestionUsage,
    QuestionPathology
  }

  interface QuestionnaireRenderData {
    checked: boolean | null
    values: any
  }

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: LifeConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const insureds = ref(props.answers.insureds)

  const questionnaire = ref<Record<string, QuestionnaireRenderData>>({
    disability: loadQuestion('disability'),
    interruptedActivity: loadQuestion('interruptedActivity'),
    sickLeave: loadQuestion('sickLeave'),
    alcohol: loadQuestion('alcohol'),
    smoking: loadQuestion('smoking'),
    drugs: loadQuestion('drugs'),
    medication: loadQuestion('medication'),
    injuries: loadQuestion('injuries')
  })

  function hasHtmlTags(text: string) {
    return /<\w+>/.test(text)
  }

  function loadQuestion(
    key: keyof Omit<LifeInsuredMedicalQuestionnaire, 'filled'>
  ): QuestionnaireRenderData {
    const firstInsured = insureds.value[0]
    const questionnaireFilled = firstInsured.questionnaire.filled
    const checked = questionnaireFilled ? !firstInsured.questionnaire[key].isEmpty() : null
    const values = firstInsured.questionnaire[key] ?? null

    return {
      checked,
      values
    }
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    insureds.value[0].questionnaire = new LifeInsuredMedicalQuestionnaire({
      disability: getValuesToSave('disability'),
      interruptedActivity: getValuesToSave('interruptedActivity'),
      sickLeave: getValuesToSave('sickLeave'),
      alcohol: getValuesToSave('alcohol'),
      smoking: getValuesToSave('smoking'),
      drugs: getValuesToSave('drugs'),
      medication: getValuesToSave('medication'),
      injuries: getValuesToSave('injuries'),
      filled: true
    })

    const answers = { insureds: insureds.value }

    return { page, answers }
  }

  function getValuesToSave(key: string) {
    const { checked, values } = questionnaire.value[key]
    if (!checked) return null

    return values
  }

  function beforeMount() {
    setTimeout(loadingStore.stopLoading, 300)
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  // .MedicalQuestionnaireForm {
  //   // border: 1px solid red;
  // }
</style>
